import { Suspense, useLayoutEffect, useState } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import {
  AppWindow,
  ArrowLeftRight,
  Box,
  CircleGauge,
  CreditCard,
  Logs,
  ScanLine,
  Settings,
  ShieldCheck,
  ShieldQuestion,
  SquareFunction,
} from 'lucide-react';
import { easeOut } from 'motion';
import {
  MotionValue,
  useMotionValueEvent,
  useScroll,
  useTransform,
} from 'motion/react';
import { Link, useMatch, useParams } from 'react-router';

import { Badge, cn } from '@components/primitives';

import { useOrigins } from '@components/features/Compliance/queries';
import { AccountDropdown } from '@components/features/Layout/Navigation/AccountDropdown';
import { ComplianceCount } from '@components/features/Layout/Navigation/ComplianceCount';
import { Logo } from '@components/features/Layout/Navigation/Logo';
import { PageTabs } from '@components/features/Layout/Navigation/PageTabs';
import {
  AdminBreadcrumbs,
  AdminLinks,
} from '@components/features/Layout/Navigation/variants/admin';
import {
  TeamAppBreadcrumbs,
  TeamAppLinks,
} from '@components/features/Layout/Navigation/variants/team-app';
import { useAppQuery } from '@queries/app';

import css from './styles.module.css';

function TopRow() {
  const { teamUuid, appUuid } = useParams();

  let rootHref = '/';
  if (teamUuid) {
    rootHref = `/${teamUuid}`;
    if (appUuid) {
      rootHref = `/${teamUuid}/${appUuid}`;
    }
  }

  const isAdmin = useMatch('/admin/*');

  return (
    <>
      <Link to={rootHref} className={css.stickyLogo}>
        <div className={css.logo}>
          <Logo className={css.logoIcon} aria-hidden />
        </div>
        <span className="sr-only">Evervault</span>
      </Link>

      <nav className={cn(css.row, css.topRow)}>
        <div className={css.rowSection}>
          <div className={css.logoPlaceholder} />
          <div className={css.dropdowns}>
            <Suspense fallback={null}>
              {isAdmin ? <AdminBreadcrumbs /> : <TeamAppBreadcrumbs />}
            </Suspense>
          </div>
        </div>

        <div className={css.rowSection} data-no-shrink>
          <Suspense fallback={null}>
            {isAdmin ? <AdminLinks /> : <TeamAppLinks />}
          </Suspense>

          <AccountDropdown />
        </div>
      </nav>
    </>
  );
}

interface AppRowProps {
  leadingSpace: MotionValue<number>;
  stuck: boolean;
}

interface PageProtectionNavItemProps {
  teamUuid: string;
  appUuid: string;
}

function PageProtectionNavItem({
  teamUuid,
  appUuid,
}: PageProtectionNavItemProps) {
  const { data } = useOrigins();

  if (!data?.origins.length) return null;

  return (
    <PageTabs.Item
      to={`/${teamUuid}/${appUuid}/compliance`}
      icon={<ShieldCheck />}
      label="Page Protection"
      suffix={<ComplianceCount />}
    />
  );
}

function AppRow({ leadingSpace, stuck }: AppRowProps) {
  const { teamUuid, appUuid } = useParams();
  const { data: app } = useAppQuery(teamUuid ?? '', appUuid ?? '');
  const flags = useFlags();

  const shouldRender = !!teamUuid && !!appUuid;

  useLayoutEffect(() => {
    if (shouldRender) {
      document.body.style.setProperty('--navigation-height', '50px');
      return () => {
        document.body.style.removeProperty('--navigation-height');
      };
    }
  }, [shouldRender]);

  if (!shouldRender) return null;

  return (
    <nav
      className={cn(css.row, css.appRow)}
      aria-label="App navigation"
      data-stuck={stuck}
    >
      <PageTabs label={app?.name} leadingSpace={leadingSpace}>
        <PageTabs.Item
          to={`/${teamUuid}/${appUuid}`}
          icon={<CircleGauge />}
          label="Overview"
          keywords={['overview']}
          end
        />
        <PageTabs.Item
          to={`/${teamUuid}/${appUuid}/relays`}
          icon={<ArrowLeftRight />}
          label="Relays"
        />
        <PageTabs.Item
          to={`/${teamUuid}/${appUuid}/functions`}
          icon={<SquareFunction />}
          label="Functions"
        />
        <PageTabs.Item
          to={`/${teamUuid}/${appUuid}/enclaves`}
          icon={<Box />}
          label="Enclaves"
        />
        <PageTabs.Item
          to={`/${teamUuid}/${appUuid}/payments`}
          icon={<CreditCard />}
          label="Payments"
        />
        {flags.browserTokens && (
          <PageTabs.Item
            to={`/${teamUuid}/${appUuid}/browser-tokens`}
            icon={<AppWindow />}
            label="Browser Tokens"
            suffix={
              <Badge size="sm" variant="primary">
                New
              </Badge>
            }
          />
        )}
        <PageTabs.Item
          to={`/${teamUuid}/${appUuid}/logs`}
          icon={<Logs />}
          label="Logs"
        />
        <PageTabs.Item
          to={`/${teamUuid}/${appUuid}/policies`}
          icon={<ShieldQuestion />}
          label="Data Policies"
          requiredFlag="dataPolicies"
        />
        {flags.complianceTab && (
          <PageProtectionNavItem
            teamUuid={teamUuid ?? ''}
            appUuid={appUuid ?? ''}
          />
        )}
        <PageTabs.Item
          to={`/${teamUuid}/${appUuid}/asv`}
          icon={<ScanLine />}
          label="ASV Scans"
        />
        <PageTabs.Item
          to={`/${teamUuid}/${appUuid}/settings`}
          icon={<Settings />}
          label="Settings"
        />
      </PageTabs>
    </nav>
  );
}

export function LayoutNavigation() {
  const scroll = useScroll();
  const leadingSpace = useTransform(scroll.scrollY, [0, 36], [0, 36], {
    ease: easeOut,
  });

  const [stuck, setStuck] = useState(false);
  useMotionValueEvent(scroll.scrollY, 'change', (latest) => {
    setStuck(latest >= 36);
  });

  const isAdmin = useMatch('/admin/*');

  return (
    <>
      <TopRow />
      {!isAdmin && <AppRow stuck={stuck} leadingSpace={leadingSpace} />}
    </>
  );
}
